import { Component, KeyboardEvent, FormEventHandler } from 'react'
import { connect } from 'react-redux'
import { Field, reduxForm, change } from 'redux-form'
import { FORM_ONBOARDING_REGISTER, POLICY_CHECKBOX_NAME } from 'constants/forms'
import { ReduxFormProps, StoreDispatch } from 'interfaces/component'
import { getSaveButtonFormProps, onFormSubmit, parseValueToBoolean, shouldAsyncValidate } from 'helpers/formHelpers'
import * as styles from 'styles/molecules/forms/OnboardingForm.css'
import schema, { EMAIL_SCHEMA, policyValidatorCreator } from 'schemas/registerFormSchema'
import { asyncValidator } from 'helpers/schemaHelpers'
import TextFieldWrapped from 'components/atoms/inputs/TextFieldWrapped'
import Button from 'components/features/pfm/Button/Button'
import { checkEmailExistenceActionCreator } from 'actions/onboardingActions'
import PasswordFieldContainer from 'components/features/pfm/PasswordField/PasswordFieldContainer'
import Recaptcha from 'components/atoms/Recaptcha'
import { FIELD_CONFIRM_PASSWORD_PROPS, FIELD_EMAIL_PROPS, FIELD_FIRST_NAME_PROPS, FIELD_LAST_NAME_PROPS } from 'constants/fields'
import { executeRecaptchaActionCreator } from 'actions/recaptchaActions'
import { RECAPTCHA_FIELD_NAME } from 'constants/api'
import { RegisterFormValues } from 'interfaces/onboarding'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import CheckboxWrapped from 'components/atoms/inputs/CheckboxWrapped'
import { ENFORCE_POLICY_AGREEMENT } from 'constants/onboarding'
import CardLayout from 'components/layouts/CardLayout'

const allFieldsValidator = asyncValidator(schema)
const emailValidator = asyncValidator(EMAIL_SCHEMA)
const FORM_NAME = FORM_ONBOARDING_REGISTER

function asyncValidate (values: RegisterFormValues, dispatch: StoreDispatch, _props: ReduxFormProps<RegisterFormValues>, blurredField: string): Promise<any> {
  if (blurredField === 'email') {
    const allValidation = () => allFieldsValidator(values)

    return emailValidator(values)
      .then(() => dispatch(checkEmailExistenceActionCreator(values['email'])))
      .then(allValidation, allValidation)
  }

  return allFieldsValidator(values)
}

const POLICY_FIELD_PROPS = {
  name: POLICY_CHECKBOX_NAME,
  component: CheckboxWrapped,
  props: {
    label: (
      <span>
        By checking this box, you agree&nbsp;
        <span className={ styles.policyLabelLinks }>
          to our <NavigationLink to='terms'>Terms</NavigationLink>
          &nbsp;and&nbsp;
          <NavigationLink to='privacy'>Privacy Policy</NavigationLink>
        </span>
      </span>
    ),
    qaMarker: 'policy-checkbox',
  },
  validate: policyValidatorCreator(true) as (value: any) => string | undefined,
  parse: parseValueToBoolean,
}

type RegisterFormDispatchProps = {
  executeRecaptcha: typeof executeRecaptchaActionCreator,
  change: typeof change
}

type RegisterFormOwnProps = {
  mode?: 'regular' | 'meeting-panel' | 'credit-card'
}

type RegisterFormProps =
  & ReduxFormProps<RegisterFormValues>
  & RegisterFormDispatchProps
  & RegisterFormOwnProps

@reduxForm({
  form: FORM_ONBOARDING_REGISTER,
  asyncValidate: asyncValidate,
  shouldAsyncValidate,
  onSubmit: onFormSubmit,
})
export class RegisterFormComponent extends Component<RegisterFormProps> {

  private get mode () {
    return this.props.mode ?? 'regular'
  }


  private handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    switch (event.key) {
      case 'Enter':
        event.preventDefault()
        this.handleSubmit(event)
        return

      default:
        return
    }
  }


  private renderFields () {
    return (
      <ul className={ styles.controlList }>
        <li className={ styles.controlListItem }>
          <Field
            name={ RECAPTCHA_FIELD_NAME }
            component={ Recaptcha }
            onKeyDown={ this.handleKeyDown }
            props={{
              placement: FORM_NAME,
              onResponse: this.props.handleSubmit,
            }}
          />
          <Field
            name='first_name'
            component={ TextFieldWrapped }
            onKeyDown={ this.handleKeyDown }
            props={{
              ...FIELD_FIRST_NAME_PROPS,
              autoFocus: true,
            }}
          />
        </li>
        <li className={ styles.controlListItem }>
          <Field
            name='last_name'
            component={ TextFieldWrapped }
            onKeyDown={ this.handleKeyDown }
            props={{
              ...FIELD_LAST_NAME_PROPS,
            }}
          />
        </li>
        <li className={ styles.controlListItem }>
          <Field
            name='email'
            component={ TextFieldWrapped }
            onKeyDown={ this.handleKeyDown }
            props={{
              ...FIELD_EMAIL_PROPS,
            }}
          />
        </li>
        <li className={ styles.controlListItem }>
          <PasswordFieldContainer />
        </li>
        <li className={ styles.controlListItem }>
          <Field
            name='password_confirm'
            component={ TextFieldWrapped }
            onKeyDown={ this.handleKeyDown }
            props={ FIELD_CONFIRM_PASSWORD_PROPS }
          />
        </li>
        { this.renderPolicyCheckbox() }
      </ul>
    )
  }

  private renderRegularForm () {
    return (
      <>
        <CardLayout className={ styles.cardLayout }>
          { this.renderFields() }
        </CardLayout>
        <Button { ...getSaveButtonFormProps(this.props) } submit fullWidth qaMarker='submit-button'>
          Create Your Account
        </Button>
      </>
    )
  }

  private renderFormContent () {
    if (this.mode !== 'meeting-panel' && this.mode !== 'credit-card') {
      return this.renderRegularForm()
    }

    return this.renderFields()
  }

  render () {

    return (
      <form className={ styles.onboardingForm } onSubmit={ this.handleSubmit }>
        { this.renderFormContent() }
      </form>
    )
  }

  componentDidMount () {
    this.props.change('form_mode', this.mode)
  }

  renderPolicyCheckbox = () => {
    return ENFORCE_POLICY_AGREEMENT
      ? (
        <li className={ styles.controlListItem }>
          <Field { ...POLICY_FIELD_PROPS } />
        </li>
      ) : null

  }

  handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    // actual form submit will happen after successful captcha pass
    this.props.executeRecaptcha(FORM_NAME)
  }
}

const mapDispatchToProps: RegisterFormDispatchProps = {
  executeRecaptcha: executeRecaptchaActionCreator,
  change,
}

export const RegisterForm = connect<
  {},
  RegisterFormDispatchProps,
  RegisterFormOwnProps
>(undefined, mapDispatchToProps, undefined, { forwardRef: true })(RegisterFormComponent)

export default RegisterForm
